import { ReactNode } from "react";
import { Typography } from "@mui/material";
import AppModal from "./AppModal";
import { ModalTone } from "./AppModal.tokens";
import { bodyTextSx } from "./AppModal.styles";

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  /** The question, e.g. "Void this document?". */
  title: ReactNode;
  /** The consequence. Plain string, or any node for richer copy. */
  message?: ReactNode;
  subtitle?: ReactNode;
  /** Verb-first, e.g. "Delete document". Never "OK" or "Yes". */
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  onConfirm: () => void;
  /** Defaults to `onClose`. */
  onCancel?: () => void;
  /**
   * `destructive` turns the tile and the primary button red and stops a scrim
   * click from dismissing. `neutral` is the informational confirm.
   */
  tone?: Extract<ModalTone, "default" | "destructive" | "neutral">;
  loading?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  /** Confirms are S-width unless the copy genuinely needs more room. */
  size?: "sm" | "md";
  /** Extra content under the message — a checkbox, a warning list, an input. */
  children?: ReactNode;
}

/**
 * The confirm / destructive preset of the Modal System.
 *
 * It is not a second modal implementation — it is `AppModal` with the two-button
 * footer and the prose body already wired, because that shape accounts for most
 * overlays in the app. Anything that needs more than a message and two buttons
 * should use `AppModal` directly.
 */
const ConfirmModal = ({
  open,
  onClose,
  title,
  message,
  subtitle,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  tone = "default",
  loading = false,
  disabled = false,
  icon,
  size = "sm",
  children,
}: ConfirmModalProps) => (
  <AppModal
    open={open}
    onClose={onClose}
    title={title}
    subtitle={subtitle}
    icon={icon}
    tone={tone}
    size={size}
    busy={loading}
    primaryLabel={confirmLabel}
    onPrimary={onConfirm}
    primaryLoading={loading}
    primaryDisabled={disabled}
    cancelLabel={cancelLabel}
    onCancel={onCancel ?? onClose}
  >
    {Boolean(message) && <Typography sx={bodyTextSx}>{message}</Typography>}
    {children}
  </AppModal>
);

export default ConfirmModal;
